import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getSubscriptionPlans,
  getSubscription,
  getBillingMe,
  createCheckoutSession,
  logConversionEvent,
  SubscriptionPlan,
  Subscription,
} from '../services/api';
import PricingCard from './PricingCard';
import SocialProof from './Billing/SocialProof';

interface PricingPageProps {
  onClose?: () => void;
  onSuccess?: () => void;
  embedded?: boolean;
  // Marketing mode (public landing): render a presentational plan card with a
  // single "register for free" CTA - no billing-period toggle, no checkout
  // button, no social-proof/footer chrome. Checkout flows (BillingPage) leave
  // this off and keep the full interactive page.
  marketing?: boolean;
  onMarketingCta?: () => void;
  marketingCtaLabel?: string;
}

const PricingPage: React.FC<PricingPageProps> = ({
  onClose,
  onSuccess,
  embedded = false,
  marketing = false,
  onMarketingCta,
  marketingCtaLabel,
}) => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [currentSubscription, setCurrentSubscription] =
    useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectingPlan, setSelectingPlan] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [billingAnnual, setBillingAnnual] = useState(false);
  const [checkoutProvider, setCheckoutProvider] = useState<'paypal' | 'stripe'>('stripe');
  // Entitlement gate (SPEC §5 H1): never nag paying/comped users. `null` until
  // resolved - awaited before `loading` clears so paid/comped users never see a
  // hype flash. Trialing users still see the hype (conversion urgency, Fix B).
  const [entitlement, setEntitlement] = useState<{
    isComped: boolean;
    isPaid: boolean;
    isTrialing: boolean;
  } | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    loadData();
    // Funnel telemetry (PR-C5). Fire-and-forget; swallow failures so a missing
    // endpoint never breaks the page.
    try {
      logConversionEvent?.('pricing_view');
    } catch {
      /* telemetry is best-effort */
    }
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const plansResponse = await getSubscriptionPlans();

      // Marketing mode renders on the public (unauthenticated) landing page and
      // ignores subscription/entitlement entirely, so skip those two authed
      // calls - they would 401 and just add server-log noise.
      const [subscriptionResponse, meResult] = marketing
        ? [
            {
              subscription: null,
              plan: 'free',
              status: 'inactive',
              freeScansRemaining: 10,
            },
            null,
          ]
        : await Promise.all([
            getSubscription().catch(() => ({
              subscription: null,
              plan: 'free',
              status: 'inactive',
              freeScansRemaining: 10,
            })),
            // Learn the active checkout provider (401 for anon → null → stripe)
            // and whether the user is already paying/comped/trialing (H1 - gate
            // the hype). Awaited so the hype never flashes before it resolves.
            getBillingMe().catch(() => null),
          ]);

      if (meResult) {
        setCheckoutProvider(meResult.checkoutProvider);
        const isComped = !!meResult.is_comped;
        const isTrialing = !!meResult.is_trialing;
        const isPaid =
          !isComped && !isTrialing && meResult.entitlement_tier !== 'free';
        setEntitlement({ isComped, isPaid, isTrialing });
      } else {
        setCheckoutProvider('stripe');
        setEntitlement({ isComped: false, isPaid: false, isTrialing: false });
      }

      setPlans(plansResponse.plans ?? []);
      setCurrentSubscription(subscriptionResponse.subscription);
    } catch (err: unknown) {
      console.error('Error loading pricing data:', err);
      setError('Failed to load pricing information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const anyYearly = useMemo(
    () => plans.some((p) => (p.yearlyPrice ?? 0) > 0 && p.monthlyPrice > 0),
    [plans]
  );

  const fanFavoritePlanId = useMemo(() => {
    const paid = plans.filter((p) => p.monthlyPrice > 0);
    const first = paid[0];
    if (!first) return null;
    return first.id;
  }, [plans]);

  // The first paid plan, used to source REAL stats for the social-proof strip.
  const proPlan = useMemo(
    () => plans.find((p) => p.monthlyPrice > 0) ?? null,
    [plans]
  );

  // Show persuasion hype only once entitlement resolves, and only to users who
  // are not paid/comped (SPEC §5 H1). Trialing users keep the hype for
  // conversion urgency (Fix B).
  const showHype =
    entitlement != null && !entitlement.isPaid && !entitlement.isComped;

  const handleSelectPlan = async (plan: SubscriptionPlan) => {
    if (plan.id === 'free') {
      if (onSuccess) onSuccess();
      return;
    }

    // Funnel telemetry (PR-C5): a paid-plan CTA is initiating checkout.
    // For PayPal, checkout_started is logged when the PayPal button is actually
    // clicked (PayPalSubscribeButton), not on this redirect.
    if (checkoutProvider !== 'paypal') {
      try {
        logConversionEvent?.('checkout_started');
      } catch {
        /* telemetry is best-effort */
      }
    }

    // PayPal is the active provider → hand off to the authenticated Billing
    // tab (which hosts the PayPal flow + monthly/yearly toggle). This is what
    // makes the annual option actually charge annually (fixes the cosmetic
    // Stripe yearly toggle). Uses the app's ?tab= deep-link contract.
    if (checkoutProvider === 'paypal') {
      const url = new URL(window.location.href);
      url.searchParams.set('tab', 'billing');
      window.location.assign(url.toString());
      return;
    }

    try {
      setSelectingPlan(true);
      setError(null);

      const stripePriceId = plan.stripePriceId?.trim();
      const result = await createCheckoutSession(
        stripePriceId || undefined,
        plan.trialDays
      );

      if (result.url) {
        window.location.href = result.url;
      } else {
        setError('Failed to create checkout session. Please try again.');
      }
    } catch (err: unknown) {
      console.error('Error creating checkout session:', err);
      const ax = err as { response?: { data?: { error?: string } } };
      setError(
        ax.response?.data?.error ||
          'Failed to start checkout. Please try again.'
      );
    } finally {
      setSelectingPlan(false);
    }
  };

  if (loading) {
    return (
      <div className="py-10 text-center">
        <div className="text-text-secondary">Loading plans…</div>
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'mx-auto max-w-[1100px] px-5 py-10'}>
      {!embedded && onClose && (
        <button
          type="button"
          onClick={onClose}
          className="absolute top-0 end-0 m-3 text-text-muted"
          aria-label="Close pricing"
        >
          ×
        </button>
      )}

      {!embedded && showHype && (
        <div className="text-center mb-8">
          <p className="uppercase text-xs font-semibold tracking-[0.08em] text-accent-primary mb-2">
            {t('pricing.eyebrow', 'Launch pricing')}
          </p>
          <h1 className="font-display text-3xl md:text-4xl text-text-primary mb-3">
            {t('pricing.heroTitle', 'The analytical edge for serious investors')}
          </h1>
          <p className="text-text-secondary max-w-xl mx-auto mb-6">
            {t(
              'pricing.heroSub',
              'AI scores, technical signals, and full-universe screening.'
            )}
          </p>
        </div>
      )}

      {!embedded && !showHype && (
        <div className="text-center mb-8">
          <h1 className="font-display text-3xl md:text-4xl text-text-primary mb-3">
            {t('pricing.alreadyProTitle', "You're on Pro")}
          </h1>
          <p className="text-text-secondary max-w-xl mx-auto">
            {t(
              'pricing.alreadyProSub',
              'You have full access. Manage your subscription from the Billing tab.'
            )}
          </p>
        </div>
      )}

      {!marketing && anyYearly && (
        <div className="flex justify-center mb-6">
          <div
            className="inline-flex items-center gap-1 rounded-full bg-surface-container-high p-1"
            role="group"
            aria-label="Billing period"
          >
            <button
              type="button"
              className={`rounded-full px-4 py-1 text-sm font-semibold transition-colors ${!billingAnnual ? 'bg-accent-primary text-on-primary' : 'text-text-secondary hover:text-text-primary'}`}
              onClick={() => setBillingAnnual(false)}
            >
              Monthly
            </button>
            <button
              type="button"
              className={`rounded-full px-4 py-1 text-sm font-semibold transition-colors ${billingAnnual ? 'bg-accent-primary text-on-primary' : 'text-text-secondary hover:text-text-primary'}`}
              onClick={() => setBillingAnnual(true)}
            >
              Annual
            </button>
          </div>
        </div>
      )}

      {currentSubscription?.status === 'trialing' && (
        <div className="mb-6 p-3 rounded-xl text-center text-sm border border-accent-bullish/35 bg-accent-bullish/12 text-accent-bullish">
          Trial ends{' '}
          {new Date(currentSubscription.trialEnd!).toLocaleDateString()} -
          upgrade to stay on a paid tier.
        </div>
      )}

      {error && (
        <div className="mb-6 p-3 rounded-xl text-center text-sm border border-accent-bearish/35 bg-accent-bearish/12 text-accent-bearish">
          {error}
        </div>
      )}

      {/* ponytail: Free is no longer a usable tier (14-day trial → Pro), so the
          Free card is hidden - only the paid Pro plan is offered. Single card is
          centered. If a 2nd paid plan is added, restore sm:grid-cols-2. */}
      <div
        className={`mx-auto grid max-w-sm grid-cols-1 justify-items-center gap-4 ${embedded ? '' : 'mb-8'}`}
      >
        {plans.filter((plan) => plan.id !== 'free').map((plan) => (
          <div key={plan.id} className="w-full max-w-sm">
            <PricingCard
              plan={plan}
              isCurrentPlan={currentSubscription?.planId === plan.id}
              onSelect={handleSelectPlan}
              isLoading={selectingPlan}
              billingAnnual={billingAnnual && anyYearly}
              fanFavorite={
                plan.id === fanFavoritePlanId && plan.monthlyPrice > 0
              }
              marketing={marketing}
              onMarketingCta={onMarketingCta}
              marketingCtaLabel={marketingCtaLabel}
            />
          </div>
        ))}
      </div>

      {!marketing && showHype && (
        <>
          <div
            className="flex flex-wrap justify-center gap-2 md:gap-3 mb-4"
            role="group"
            aria-label={t('pricing.trustStripLabel', 'Why you can buy with confidence')}
          >
            {[
              t('pricing.trustTrial', '{{days}}-day free trial', {
                days: 14,
              }),
              t('pricing.trustCancel', 'Cancel anytime'),
              t('pricing.trustSecure', 'Secure checkout - Stripe & PayPal'),
            ].map((chip) => (
              <span
                key={chip}
                className="rounded-full bg-surface-container-high px-3 py-2 text-xs font-medium text-text-secondary"
              >
                {chip}
              </span>
            ))}
          </div>

          <div className="mb-4">
            <SocialProof proPlan={proPlan} />
          </div>
        </>
      )}

      {!marketing && (
        <div className="text-center text-text-muted text-sm mt-4">
          <p className="mb-1">
            Every feature is free for your first 14 days. After the trial,
            subscribe to Pro to keep going. AI analysis uses monthly AI credits.
          </p>
          <p className="mb-0">
            Questions?{' '}
            <a
              href="mailto:support@stockbai.com"
              className="text-accent-primary"
            >
              support@stockbai.com
            </a>
          </p>
        </div>
      )}
    </div>
  );
};

export default PricingPage;
